import React, { useState, useEffect } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const SECRET_STORAGE_KEY = 'foxino_api_secret';

const createSessionId = () => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createContentItemId = () => `content-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Helper function to extract relative path from absolute path
const getRelativePath = (absolutePath: string): string => {
  if (!absolutePath) return '';
  
  // If it's already a relative path, return as is
  if (!absolutePath.startsWith('/')) {
    return absolutePath;
  }
  
  // Extract relative path from absolute path
  // Look for common patterns like /server/videos_with_audio/ or /server/generated_videos/
  const serverPattern = /\/server\/(videos_with_audio|generated_videos|generated_images)\/(.+)$/;
  const match = absolutePath.match(serverPattern);
  
  if (match) {
    return `${match[1]}/${match[2]}`;
  }
  
  // Handle full absolute paths that contain the project structure
  // Look for patterns like /Users/.../foxino-ml-video-style-generator/server/videos_with_audio/
  const fullPathPattern = /.*\/foxino-ml-video-style-generator\/server\/(videos_with_audio|generated_videos|generated_images)\/(.+)$/;
  const fullMatch = absolutePath.match(fullPathPattern);
  
  if (fullMatch) {
    return `${fullMatch[1]}/${fullMatch[2]}`;
  }
  
  // Fallback: try to extract filename from path
  const filename = absolutePath.split('/').pop();
  return filename || absolutePath;
};

interface SceneConcept {
  name: string;
  description: string;
  note?: string;
}

type ContentGenerationMode = 'single_word' | 'scenario';

interface ContentItem {
  id: string;
  mode: ContentGenerationMode;
  word?: string;
  topic?: string;
  objectTerm?: string;
  activityTerm?: string;
  placeTerm?: string;
  enabled: boolean;
}

interface ContentRequestPayload {
  content_mode: ContentGenerationMode;
  word?: string;
  topic?: string;
  object_term?: string;
  activity_term?: string;
  place_term?: string;
}

interface StyleParameterPayload {
  color_intensity: number;
  story_energy: number;
  surprise_factor: number;
}

interface GenerationObject {
  id: number;
  content_mode: ContentGenerationMode;
  concept_title: string;
  concept_description: string;
  word: string | null;
  topic: string | null;
  filename_prefix: string;
  status: string;
  object_term?: string | null;
  activity_term?: string | null;
  place_term?: string | null;
  image_prompt?: string | null;
  video_prompt?: string | null;
  audio_prompt?: string | null;
  image_path?: string | null;
  video_path?: string | null;
  audio_path?: string | null;
  error_message?: string | null;
  created_at: Date;
  updated_at: Date;
}

interface JobStatus {
  job_id: string;
  status: string;
  total_objects: number;
  completed_objects: number;
  failed_objects: number;
  cancelled_objects: number;
  generation_objects: GenerationObject[];
}

type ConceptMode = 'ai' | 'manual' | 'preset';

const PREDEFINED_STYLES: SceneConcept[] = [
  {
    name: 'Pixar 3D Style',
    description: 'Vibrant, character-driven 3D scenes with cinematic lighting reminiscent of Pixar.'
  },
  {
    name: 'Photorealistic',
    description: 'High fidelity visuals that aim to mimic real-world lighting, textures, and depth.'
  },
  {
    name: 'Tim Burton Style',
    description: 'Moody, gothic compositions with elongated forms and high contrast palettes.'
  },
  {
    name: 'Claymation Look',
    description: 'Hand-crafted clay textures and stop-motion inspired character designs.'
  },
  {
    name: 'Cartoon 2D',
    description: 'Flat, bold line art with energetic poses and Saturday-morning cartoon energy.'
  },
  {
    name: 'Miniature Realism',
    description: 'Detailed diorama-like environments that feel like miniature sets.'
  },
  {
    name: 'Neon Cyberpunk',
    description: 'Electric neon palettes, dense futuristic cityscapes, and glitch aesthetics.'
  },
  {
    name: 'Watercolor Fantasy',
    description: 'Soft watercolor washes with dreamy fantasy motifs and flowing shapes.'
  },
  {
    name: 'Dreamcore Pastel',
    description: 'Pastel surrealism with hazy lighting and dreamlike, comforting imagery.'
  },
  {
    name: 'Retro Cartoon 80s',
    description: 'Chunky 80s animation style with grainy textures and nostalgic palettes.'
  },
  {
    name: 'Surreal Dreamscape',
    description: 'Floating landscapes, unexpected scale, and surreal atmospheric lighting.'
  },
  {
    name: 'Stop-Motion Plasticine',
    description: 'Tactile plasticine figures animated with stop-motion charm.'
  },
  {
    name: 'Low-Poly 3D',
    description: 'Stylized low-poly geometry with simplified shading and sharp silhouettes.'
  },
  {
    name: 'Paper Cut-Out',
    description: 'Layered paper textures with cut-out characters and handcrafted edges.'
  },
  {
    name: 'Fantasy Illustration',
    description: 'Epic fantasy compositions with painterly brushwork and luminous effects.'
  },
  {
    name: 'Comic Book Style',
    description: 'Bold inking, half-tone shading, and dynamic comic panel drama.'
  },
  {
    name: "Children's Book Art",
    description: 'Gentle, inviting illustrations with playful proportions and warm palettes.'
  },
  {
    name: 'Plastic Toy Realism',
    description: 'Glossy plastic materials that feel like collectible figurines brought to life.'
  },
  {
    name: 'Doodle Sketch',
    description: 'Loose hand-drawn lines, scribbled shading, and playful spontaneity.'
  },
  {
    name: 'Hyperreal Cute 3D',
    description: 'Hyper-detailed yet adorable 3D characters with plush textures and glossy eyes.'
  }
];

const SINGLE_WORD_PRESETS: Array<{ word: string; topic: string }> = [
  { word: 'elephant', topic: 'A1 vocabulary' },
  { word: 'water', topic: 'A1 vocabulary' },
  { word: 'swim', topic: 'A1 vocabulary' },
  { word: 'write', topic: 'A1 vocabulary' },
  { word: 'scared', topic: 'A1 vocabulary' }
];

const createDefaultSingleWordItems = (): ContentItem[] =>
  SINGLE_WORD_PRESETS.map(({ word, topic }) => ({
    id: createContentItemId(),
    mode: 'single_word',
    word,
    topic,
    enabled: true
  }));

const MAX_CONTENT_ITEMS = 10;

const getContentLabel = (obj: {
  content_mode: ContentGenerationMode;
  word?: string | null;
  object_term?: string | null;
  activity_term?: string | null;
  place_term?: string | null;
}) => {
  if (obj.content_mode === 'scenario') {
    return (obj.object_term ?? '').trim() || 'Scenario';
  }
  return (obj.word ?? '').trim() || 'Word';
};

const getScenarioSummary = (
  activityTerm?: string | null,
  placeTerm?: string | null
) => {
  const activity = (activityTerm ?? '').trim();
  const place = (placeTerm ?? '').trim();
  if (activity && place) {
    return `${activity} in ${place}`;
  }
  return activity || place || '';
};

const GeminiSparklesIcon: React.FC = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="geminiSparklesGradient" x1="6" y1="6" x2="30" y2="30" gradientUnits="userSpaceOnUse">
        <stop stopColor="#8C7CFF" />
        <stop offset="0.5" stopColor="#5E9BFF" />
        <stop offset="1" stopColor="#3DE0FF" />
      </linearGradient>
    </defs>
    <path
      d="M17.5 6.5c.3-.9 1.7-.9 2 0l1.6 4.8 5 .2c1 0 1.4 1.3.6 1.9l-4 3.1 1.4 4.9c.3.9-.7 1.7-1.5 1.1l-4.1-2.8-4.1 2.8c-.8.6-1.8-.2-1.5-1.1l1.4-4.9-4-3.1c-.8-.6-.4-1.9.6-1.9l5-.2 1.6-4.8Z"
      fill="url(#geminiSparklesGradient)"
      opacity="0.85"
    />
    <path
      d="M9.5 14.5c.2-.6 1-.6 1.1 0l.8 2.4 2.5.1c.6 0 .8.7.3 1l-2 1.5.7 2.5c.2.6-.6 1-.9.7l-2-1.4-2 1.4c-.3.3-1.1-.1-.9-.7l.7-2.5-2-1.5c-.5-.3-.3-1 .3-1l2.5-.1.8-2.4Z"
      fill="url(#geminiSparklesGradient)"
      opacity="0.65"
    />
    <path
      d="M26 11.2c.1-.4.6-.4.7 0l.6 1.8 1.9.1c.4 0 .6.6.2.8l-1.5 1.1.5 1.9c.1.4-.5.7-.7.5l-1.6-1.1-1.6 1.1c-.3.2-.8-.1-.7-.5l.5-1.9-1.5-1.1c-.4-.2-.2-.8.2-.8l1.9-.1.6-1.8Z"
      fill="url(#geminiSparklesGradient)"
      opacity="0.5"
    />
  </svg>
);

const App: React.FC = () => {
  const [apiSecret, setApiSecret] = useState('');
  const [secretInput, setSecretInput] = useState('');
  const [secretOverlayVisible, setSecretOverlayVisible] = useState(true);
  const [secretError, setSecretError] = useState<string | null>(null);
  const [theme, setTheme] = useState('');
  const [conceptMode, setConceptMode] = useState<ConceptMode>('ai');
  const [concepts, setConcepts] = useState<SceneConcept[]>([]);
  const [selectedConcepts, setSelectedConcepts] = useState<SceneConcept[]>([]);
  const [manualConceptTitle, setManualConceptTitle] = useState('');
  const [manualConceptDescription, setManualConceptDescription] = useState('');
  const [colorIntensity, setColorIntensity] = useState<number>(50);
  const [storyEnergy, setStoryEnergy] = useState<number>(50);
  const [surpriseFactor, setSurpriseFactor] = useState<number>(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conceptsToExport, setConceptsToExport] = useState<SceneConcept[]>([]);
  const [conceptNotes, setConceptNotes] = useState<Partial<Record<string, string>>>({});
  const [savingConcepts, setSavingConcepts] = useState(false);
  const [exportingConcepts, setExportingConcepts] = useState(false);
  const [cancellingConcepts, setCancellingConcepts] = useState<Record<string, boolean>>({});
  const [contentMode, setContentMode] = useState<ContentGenerationMode>('single_word');
  const [scenarioFlow, setScenarioFlow] = useState<'manual' | 'random'>('manual');
  const [contentItems, setContentItems] = useState<ContentItem[]>(() => createDefaultSingleWordItems());
  const [newWord, setNewWord] = useState('');
  const [newScenarioObject, setNewScenarioObject] = useState('');
  const [newScenarioActivity, setNewScenarioActivity] = useState('');
  const [newScenarioPlace, setNewScenarioPlace] = useState('');
  const [scenarioObjects, setScenarioObjects] = useState<string[]>([]);
  const [scenarioActivities, setScenarioActivities] = useState<string[]>([]);
  const [scenarioPlaces, setScenarioPlaces] = useState<string[]>([]);
  const [randomObjectInput, setRandomObjectInput] = useState('');
  const [randomActivityInput, setRandomActivityInput] = useState('');
  const [randomPlaceInput, setRandomPlaceInput] = useState('');
  const [randomScenarioCount, setRandomScenarioCount] = useState<number>(5);
  const [cancelling, setCancelling] = useState(false);
  const [downloadingConcepts, setDownloadingConcepts] = useState<Record<string, boolean>>({});
  const [sessionId, setSessionId] = useState<string>(() => createSessionId());
  const sliderBackground = (value: number): React.CSSProperties => ({
    background: `linear-gradient(90deg, #667eea 0%, #764ba2 ${value}%, #e5e7eb ${value}%, #e5e7eb 100%)`
  });
  const conceptModeOptions: Array<{
    value: ConceptMode;
    title: string;
    description: string;
    icon?: React.ReactNode;
  }> = [
    {
      value: 'ai',
      title: 'AI-Suggested Styles',
      description: 'Describe a theme and let the AI propose fresh style concepts.',
      icon: <GeminiSparklesIcon />
    },
    {
      value: 'manual',
      title: 'Manual Entry',
      description: 'Type custom concept titles and descriptions tailored to your project.'
    },
    {
      value: 'preset',
      title: 'Style Library',
      description: 'Pick from the curated Foxino library of ready-to-use visual styles.'
    }
  ];

  const applySecret = (secret: string, persist = true) => {
    const trimmed = secret.trim();

    if (trimmed) {
      axios.defaults.headers.common['X-Foxino-Token'] = trimmed;
      if (persist) {
        localStorage.setItem(SECRET_STORAGE_KEY, trimmed);
      }
    } else {
      delete axios.defaults.headers.common['X-Foxino-Token'];
      if (persist) {
        localStorage.removeItem(SECRET_STORAGE_KEY);
      }
    }

    setApiSecret(trimmed);
  };

  const promptForSecret = (message?: string, resetStored: boolean = false) => {
    if (resetStored) {
      applySecret('');
    }
    setSecretInput('');
    setSecretOverlayVisible(true);
    setSecretError(message ?? null);
    setError(null);
  };

  const ensureSecret = () => {
    if (!apiSecret) {
      promptForSecret('Please enter the shared secret to continue.');
      return false;
    }
    return true;
  };

  useEffect(() => {
    const savedSecret = localStorage.getItem(SECRET_STORAGE_KEY);
    if (savedSecret && savedSecret.trim()) {
      applySecret(savedSecret, false);
      setSecretOverlayVisible(false);
      setSecretError(null);
    } else {
      setSecretOverlayVisible(true);
    }
  }, []);

  const fetchJobStatus = async (id: string) => {
    if (!apiSecret) {
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/status/${id}`);
      setJobStatus(response.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        promptForSecret('Invalid secret. Please try again.', true);
      } else {
        console.error('Failed to fetch job status:', err);
      }
    }
  };

  const handleSecretSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!secretInput.trim()) {
      setSecretError('Please enter the shared secret.');
      return;
    }
    applySecret(secretInput);
    setSecretInput('');
    setSecretOverlayVisible(false);
    setSecretError(null);
    setError(null);
  };

  const buildMediaUrl = (path?: string | null, includeToken: boolean = true) => {
    if (!path) {
      return null;
    }
    const relativePath = getRelativePath(path);
    const baseUrl = `${API_BASE_URL}/media/${encodeURIComponent(relativePath)}`;
    if (includeToken && apiSecret) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${encodeURIComponent(apiSecret)}`;
    }
    return baseUrl;
  };

  const handleApiError = (err: unknown, fallbackMessage: string) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      promptForSecret('Invalid secret. Please try again.', true);
      return;
    }
    setError(fallbackMessage);
    console.error(err);
  };

  const handleConceptModeChange = (mode: ConceptMode) => {
    if (mode === conceptMode) {
      return;
    }
    setConceptMode(mode);
    setTheme('');
    setSessionId(createSessionId());
    setConcepts([]);
    setSelectedConcepts([]);
    setConceptsToExport([]);
    setConceptNotes({});
    setManualConceptTitle('');
    setManualConceptDescription('');
    setError(null);
  };

  const removeConceptByName = (conceptName: string) => {
    setConcepts((prev) => prev.filter((concept) => concept.name !== conceptName));
    setSelectedConcepts((prev) => prev.filter((concept) => concept.name !== conceptName));
    setConceptsToExport((prev) => prev.filter((concept) => concept.name !== conceptName));
    setConceptNotes((prev) => {
      if (!(conceptName in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[conceptName];
      return next;
    });
    setCancellingConcepts((prev) => {
      if (!(conceptName in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[conceptName];
      return next;
    });
    setDownloadingConcepts((prev) => {
      if (!(conceptName in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[conceptName];
      return next;
    });
  };

  const addManualConcept = () => {
    const title = manualConceptTitle.trim();
    const description = manualConceptDescription.trim();

    if (!title) {
      setError('Please enter a concept title');
      return;
    }

    if (!description) {
      setError('Please enter a concept description');
      return;
    }

    const newConcept: SceneConcept = { name: title, description };
    setError(null);

    setConcepts((prev) => {
      const existingIndex = prev.findIndex(
        (concept) => concept.name.toLowerCase() === title.toLowerCase()
      );
      if (existingIndex !== -1) {
        const next = [...prev];
        next[existingIndex] = newConcept;
        return next;
      }
      return [...prev, newConcept];
    });

    setSelectedConcepts((prev) => {
      const existingIndex = prev.findIndex(
        (concept) => concept.name.toLowerCase() === title.toLowerCase()
      );
      if (existingIndex !== -1) {
        const next = [...prev];
        next[existingIndex] = newConcept;
        setConceptsToExport(next);
        return next;
      }
      const next = [...prev, newConcept];
      setConceptsToExport(next);
      return next;
    });

    setManualConceptTitle('');
    setManualConceptDescription('');
  };

  const togglePresetConcept = (concept: SceneConcept) => {
    setError(null);
    const exists = concepts.some((item) => item.name === concept.name);
    if (exists) {
      removeConceptByName(concept.name);
      return;
    }

    setConcepts((prev) => [...prev, concept]);
    setSelectedConcepts((prev) => {
      if (prev.some((item) => item.name === concept.name)) {
        return prev;
      }
      const next = [...prev, concept];
      setConceptsToExport(next);
      return next;
    });
  };

  // Poll job status every 5 seconds when job is active
  useEffect(() => {
    if (jobId && jobStatus?.status === 'PROCESSING' && apiSecret) {
      const interval = setInterval(() => {
        fetchJobStatus(jobId);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [jobId, jobStatus?.status, apiSecret]);

  // Also poll when job is completed to ensure we get the final audio_path values
  useEffect(() => {
    if (
      jobId &&
      apiSecret &&
      jobStatus?.status === 'COMPLETED' &&
      jobStatus?.completed_objects < jobStatus?.total_objects
    ) {
      const interval = setInterval(() => {
        fetchJobStatus(jobId);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [jobId, apiSecret, jobStatus?.status, jobStatus?.completed_objects, jobStatus?.total_objects]);

  useEffect(() => {
    if (!jobId || !apiSecret) {
      return;
    }

    const cancelIfActive = () => {
      const status = jobStatus?.status;
      if (status === 'COMPLETED' || status === 'FAILED') {
        return;
      }

      const url = `${API_BASE_URL}/cancel_job/${jobId}`;

      fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'X-Foxino-Token': apiSecret
        },
        body: '{}'
      }).catch(() => {
        // Ignore network errors triggered during page unload
      });
    };

    window.addEventListener('pagehide', cancelIfActive);
    window.addEventListener('beforeunload', cancelIfActive);
    return () => {
      window.removeEventListener('pagehide', cancelIfActive);
      window.removeEventListener('beforeunload', cancelIfActive);
    };
  }, [jobId, apiSecret, jobStatus?.status]);

  const generateConcepts = async () => {
    if (!ensureSecret()) {
      return;
    }
    if (!theme.trim()) {
      setError('Please enter a theme');
      return;
    }

    setLoading(true);
    setError(null);
    setSessionId(createSessionId());

    try {
      const response = await axios.post(`${API_BASE_URL}/propose_scene_concepts`, {
        theme: theme.trim()
      });
      setSelectedConcepts([]);
      setConceptsToExport([]);
      setConceptNotes({});
      setConcepts(response.data.concepts);
    } catch (err) {
      handleApiError(err, 'Failed to generate concepts');
    } finally {
      setLoading(false);
    }
  };

  const submitJob = async () => {
    if (!ensureSecret()) {
      return;
    }
    if (selectedConcepts.length === 0) {
      setError('Please select at least one concept');
      return;
    }

    const enabledItems = contentItems.filter(item => item.enabled);
    if (enabledItems.length === 0) {
      setError('Please select at least one item to generate');
      return;
    }

    const invalidItem = enabledItems.find(item => {
      if (contentMode === 'single_word') {
        return !(item.word && item.word.trim());
      }
      return !(
        item.objectTerm && item.objectTerm.trim() &&
        item.activityTerm && item.activityTerm.trim() &&
        item.placeTerm && item.placeTerm.trim()
      );
    });

    if (invalidItem) {
      setError(
        contentMode === 'single_word'
          ? 'Please ensure all words have a value'
          : 'Please ensure each scenario includes an object, activity, and place'
      );
      return;
    }

    const contentPayload = enabledItems.map<ContentRequestPayload>((item) => {
      if (contentMode === 'single_word') {
        const wordValue = (item.word ?? '').trim();
        const topicValue = (item.topic ?? 'A1 vocabulary').trim() || 'A1 vocabulary';
        return {
          content_mode: 'single_word',
          word: wordValue,
          topic: topicValue
        };
      }
      return {
        content_mode: 'scenario',
        object_term: (item.objectTerm ?? '').trim(),
        activity_term: (item.activityTerm ?? '').trim(),
        place_term: (item.placeTerm ?? '').trim()
      };
    });

    setLoading(true);
    setError(null);

    try {
      const styleParametersPayload: StyleParameterPayload = {
        color_intensity: colorIntensity,
        story_energy: storyEnergy,
        surprise_factor: surpriseFactor
      };
      const response = await axios.post(`${API_BASE_URL}/submit_job`, {
        selected_concepts: selectedConcepts,
        content_items: contentPayload,
        style_parameters: styleParametersPayload
      });
      setJobId(response.data.job_id);
      setSessionId(response.data.job_id);
      
      // Create initial job status with placeholders
      const initialJobStatus: JobStatus = {
        job_id: response.data.job_id,
        status: 'PROCESSING',
        total_objects: response.data.total_objects,
        completed_objects: 0,
        failed_objects: 0,
        cancelled_objects: 0,
        generation_objects: []
      };

      // Create placeholder objects for each concept-word combination
      let objectId = 1;
      const jobIdPrefix = response.data.job_id.includes('-')
        ? response.data.job_id.split('-')[0]
        : response.data.job_id.slice(0, 8);
      selectedConcepts.forEach((concept) => {
        contentPayload.forEach((payload) => {
          const wordIndex = objectId;
          const primaryTerm =
            payload.content_mode === 'single_word'
              ? (payload.word ?? '')
              : (payload.object_term ?? '');
          const safeWord = primaryTerm.trim().toLowerCase().replace(/\s+/g, '_') || 'content';
          initialJobStatus.generation_objects.push({
            id: objectId++,
            content_mode: payload.content_mode,
            concept_title: concept.name,
            concept_description: concept.description,
            word: primaryTerm,
            topic: payload.content_mode === 'single_word' ? (payload.topic ?? '') : '',
            filename_prefix: `${jobIdPrefix}_${String(wordIndex).padStart(2, '0')}_${safeWord}`,
            object_term: payload.content_mode === 'scenario' ? (payload.object_term ?? null) : null,
            activity_term: payload.content_mode === 'scenario' ? (payload.activity_term ?? null) : null,
            place_term: payload.content_mode === 'scenario' ? (payload.place_term ?? null) : null,
            status: 'QUEUE',
            image_prompt: null,
            video_prompt: null,
            audio_prompt: null,
            image_path: null,
            video_path: null,
            audio_path: null,
            error_message: null,
            created_at: new Date(),
            updated_at: new Date()
          });
        });
      });

      setJobStatus(initialJobStatus);
      setCancellingConcepts({});
      
      // Start polling for updates
      await fetchJobStatus(response.data.job_id);
    } catch (err) {
      handleApiError(err, 'Failed to submit job');
    } finally {
      setLoading(false);
    }
  };

  const toggleExportConcept = (concept: SceneConcept) => {
    setConceptsToExport(prev => 
      prev.some(c => c.name === concept.name)
        ? prev.filter(c => c.name !== concept.name)
        : [...prev, concept]
    );
  };

  const updateConceptNote = (conceptName: string, note: string) => {
    setConceptNotes(prev => ({
      ...prev,
      [conceptName]: note
    }));
  };

  const buildConceptPayload = () => {
    return conceptsToExport.map((concept) => {
      const rawNote = conceptNotes[concept.name];
      const trimmedNote = typeof rawNote === 'string' ? rawNote.trim() : '';
      return {
        name: concept.name,
        description: concept.description,
        note: trimmedNote ? trimmedNote : undefined
      };
    });
  };

  const saveConcepts = async () => {
    if (!ensureSecret()) {
      return;
    }
    if (conceptsToExport.length === 0) {
      setError('Please select at least one concept to export');
      return;
    }

    try {
      setError(null);
      setSavingConcepts(true);
      const conceptsPayload = buildConceptPayload();
      const response = await axios.post(`${API_BASE_URL}/save_concepts`, {
        concepts: conceptsPayload,
        session_id: sessionId
      });
      alert(response.data.message);
    } catch (err) {
      handleApiError(err, 'Failed to save concepts');
    } finally {
      setSavingConcepts(false);
    }
  };

  const exportConcepts = async () => {
    if (!ensureSecret()) {
      return;
    }
    if (conceptsToExport.length === 0) {
      setError('Please select at least one concept to export');
      return;
    }

    try {
      setError(null);
      setExportingConcepts(true);
      const conceptsPayload = buildConceptPayload();
      const response = await axios.post(`${API_BASE_URL}/export_concepts`, {
        concepts: conceptsPayload,
        session_id: sessionId
      });

      const blob = new Blob([response.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `approved_concepts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      handleApiError(err, 'Failed to export concepts');
    } finally {
      setExportingConcepts(false);
    }
  };

  const cancelConcept = async (conceptTitle: string) => {
    if (!ensureSecret()) {
      return;
    }
    if (!jobId) {
      return;
    }

    setCancellingConcepts((prev) => ({ ...prev, [conceptTitle]: true }));
    setError(null);

    try {
      await axios.post(`${API_BASE_URL}/cancel_prompt`, {
        job_id: jobId,
        concept_title: conceptTitle
      });
      await fetchJobStatus(jobId);
    } catch (err) {
      handleApiError(err, 'Failed to cancel prompt');
    } finally {
      setCancellingConcepts((prev) => {
        const next = { ...prev };
        delete next[conceptTitle];
        return next;
      });
    }
  };

  const resetContentInputs = () => {
    setNewWord('');
    setNewScenarioObject('');
    setNewScenarioActivity('');
    setNewScenarioPlace('');
    setRandomObjectInput('');
    setRandomActivityInput('');
    setRandomPlaceInput('');
  };

  const resetScenarioRandomInputs = () => {
    setScenarioObjects([]);
    setScenarioActivities([]);
    setScenarioPlaces([]);
    setRandomObjectInput('');
    setRandomActivityInput('');
    setRandomPlaceInput('');
    setRandomScenarioCount(3);
  };

  const handleContentModeChange = (mode: ContentGenerationMode) => {
    if (mode === contentMode) {
      return;
    }
    setContentMode(mode);
    const items = mode === 'single_word' ? createDefaultSingleWordItems() : [];
    setContentItems(items);
    setScenarioFlow('manual');
    resetContentInputs();
    resetScenarioRandomInputs();
    setError(null);
  };

  const handleScenarioFlowChange = (flow: 'manual' | 'random') => {
    if (flow === scenarioFlow) {
      return;
    }
    setScenarioFlow(flow);
    setContentItems([]);
    resetContentInputs();
    resetScenarioRandomInputs();
    setError(null);
  };

  const toggleContentItemEnabled = (index: number) => {
    setContentItems(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, enabled: !item.enabled } : item
      )
    );
  };

  const addSingleWordItem = () => {
    const trimmed = newWord.trim();
    if (!trimmed) {
      setError('Please enter a word');
      return;
    }
    if (contentItems.length >= MAX_CONTENT_ITEMS) {
      setError(`Maximum ${MAX_CONTENT_ITEMS} items allowed`);
      return;
    }
    if (contentItems.some(item => item.mode === 'single_word' && (item.word ?? '').toLowerCase() === trimmed.toLowerCase())) {
      setError('Word already exists');
      return;
    }
    setContentItems(prev => [
      ...prev,
      {
        id: createContentItemId(),
        mode: 'single_word',
        word: trimmed,
        topic: 'A1 vocabulary',
        enabled: true
      }
    ]);
    setNewWord('');
    setError(null);
  };

  const addScenarioItem = () => {
    if (scenarioFlow !== 'manual') {
      return;
    }
    const objectTrimmed = newScenarioObject.trim();
    const activityTrimmed = newScenarioActivity.trim();
    const placeTrimmed = newScenarioPlace.trim();

    if (!objectTrimmed || !activityTrimmed || !placeTrimmed) {
      setError('Please fill in object, activity, and place');
      return;
    }
    if (contentItems.length >= MAX_CONTENT_ITEMS) {
      setError(`Maximum ${MAX_CONTENT_ITEMS} items allowed`);
      return;
    }
    const signature = `${objectTrimmed.toLowerCase()}|${activityTrimmed.toLowerCase()}|${placeTrimmed.toLowerCase()}`;
    const exists = contentItems.some(item => {
      if (item.mode !== 'scenario') {
        return false;
      }
      const currentSignature = `${(item.objectTerm ?? '').toLowerCase()}|${(item.activityTerm ?? '').toLowerCase()}|${(item.placeTerm ?? '').toLowerCase()}`;
      return currentSignature === signature;
    });
    if (exists) {
      setError('Scenario already exists');
      return;
    }

    setContentItems(prev => [
      ...prev,
      {
        id: createContentItemId(),
        mode: 'scenario',
        objectTerm: objectTrimmed,
        activityTerm: activityTrimmed,
        placeTerm: placeTrimmed,
        enabled: true
      }
    ]);
    setNewScenarioObject('');
    setNewScenarioActivity('');
    setNewScenarioPlace('');
    setError(null);
  };

  const addScenarioPoolValue = (type: 'object' | 'activity' | 'place') => {
    let value = '';
    let setValue: React.Dispatch<React.SetStateAction<string>>;
    let list: string[];
    let setList: React.Dispatch<React.SetStateAction<string[]>>;
    let label = '';

    if (type === 'object') {
      value = randomObjectInput.trim();
      setValue = setRandomObjectInput;
      list = scenarioObjects;
      setList = setScenarioObjects;
      label = 'object';
    } else if (type === 'activity') {
      value = randomActivityInput.trim();
      setValue = setRandomActivityInput;
      list = scenarioActivities;
      setList = setScenarioActivities;
      label = 'activity';
    } else {
      value = randomPlaceInput.trim();
      setValue = setRandomPlaceInput;
      list = scenarioPlaces;
      setList = setScenarioPlaces;
      label = 'place';
    }

    if (!value) {
      setError(`Please enter a ${label}`);
      return;
    }

    if (list.some((item) => item.toLowerCase() === value.toLowerCase())) {
      setError(`That ${label} already exists`);
      return;
    }

    setList((prev) => [...prev, value]);
    setValue('');
    setError(null);
  };

  const removeScenarioPoolValue = (type: 'object' | 'activity' | 'place', index: number) => {
    if (type === 'object') {
      setScenarioObjects((prev) => prev.filter((_, i) => i !== index));
    } else if (type === 'activity') {
      setScenarioActivities((prev) => prev.filter((_, i) => i !== index));
    } else {
      setScenarioPlaces((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const generateRandomScenarios = () => {
    if (scenarioFlow !== 'random') {
      return;
    }
    if (!scenarioObjects.length || !scenarioActivities.length || !scenarioPlaces.length) {
      setError('Add at least one object, activity, and place');
      return;
    }
    if (!randomScenarioCount || randomScenarioCount < 1) {
      setError('Enter how many scenes to generate (1-10)');
      return;
    }

    const combinations: Array<{ object: string; activity: string; place: string }> = [];
    for (const objectTerm of scenarioObjects) {
      for (const activityTerm of scenarioActivities) {
        for (const placeTerm of scenarioPlaces) {
          combinations.push({
            object: objectTerm,
            activity: activityTerm,
            place: placeTerm
          });
          if (combinations.length > 2000) {
            break;
          }
        }
        if (combinations.length > 2000) {
          break;
        }
      }
      if (combinations.length > 2000) {
        break;
      }
    }

    if (!combinations.length) {
      setError('No combinations available. Add more words to each list.');
      return;
    }

    const shuffled = shuffleArray(combinations);
    const desiredCount = Math.min(randomScenarioCount, MAX_CONTENT_ITEMS, shuffled.length);
    const items: ContentItem[] = shuffled.slice(0, desiredCount).map((combo) => ({
      id: createContentItemId(),
      mode: 'scenario',
      objectTerm: combo.object,
      activityTerm: combo.activity,
      placeTerm: combo.place,
      enabled: true
    }));

    setContentItems(items);
    setError(null);
  };

  const removeContentItem = (index: number) => {
    setContentItems(prev => prev.filter((_, i) => i !== index));
  };

  const cancelJob = async () => {
    if (!jobId) return;
    if (!ensureSecret()) {
      return;
    }
    
    setCancelling(true);
    setError(null);
    
    try {
      await axios.post(`${API_BASE_URL}/cancel_job/${jobId}`);
      // Refresh job status to show cancelled state
      await fetchJobStatus(jobId);
    } catch (err) {
      handleApiError(err, 'Failed to cancel job');
    } finally {
      setCancelling(false);
    }
  };

  const clearAll = () => {
    setTheme('');
    setConceptMode('ai');
    setConcepts([]);
    setSelectedConcepts([]);
    setConceptsToExport([]);
    setConceptNotes({});
    setSavingConcepts(false);
    setExportingConcepts(false);
    setCancellingConcepts({});
    setJobId(null);
    setJobStatus(null);
    setError(null);
    setContentMode('single_word');
    setScenarioFlow('manual');
    setContentItems(createDefaultSingleWordItems());
    resetContentInputs();
    resetScenarioRandomInputs();
    setManualConceptTitle('');
    setManualConceptDescription('');
    setSessionId(createSessionId());
  };

  const toggleConceptSelection = (concept: SceneConcept) => {
    setSelectedConcepts(prev => {
      const newSelected = prev.some(c => c.name === concept.name)
        ? prev.filter(c => c.name !== concept.name)
        : [...prev, concept];
      
      // Initialize export selection with all selected concepts
      setConceptsToExport(newSelected);
      setConceptNotes(prevNotes => {
        if (newSelected.some(c => c.name === concept.name)) {
          return prevNotes;
        }
        const nextNotes = { ...prevNotes };
        delete nextNotes[concept.name];
        return nextNotes;
      });
      setCancellingConcepts((prevFlags) => {
        if (newSelected.some(c => c.name === concept.name)) {
          return prevFlags;
        }
        if (!(concept.name in prevFlags)) {
          return prevFlags;
        }
        const nextFlags = { ...prevFlags };
        delete nextFlags[concept.name];
        return nextFlags;
      });
      
      return newSelected;
    });
  };

  const isCancelledObject = (obj?: GenerationObject | null) => {
    if (!obj) return false;
    const message = obj.error_message?.toLowerCase() ?? '';
    return obj.status === 'FAILED' && message.includes('cancelled');
  };

  const getStatusColor = (obj?: GenerationObject | null) => {
    if (isCancelledObject(obj)) {
      return '#607D8B';
    }
    const status = obj?.status || 'QUEUE';
    switch (status) {
      case 'COMPLETED': return '#4CAF50';
      case 'FAILED': return '#F44336';
      case 'PROCESSING':
      case 'GENERATING_IMAGE':
      case 'GENERATING_VIDEO':
      case 'GENERATING_AUDIO': return '#FF9800';
      default: return '#9E9E9E';
    }
  };

  const getStatusText = (obj?: GenerationObject | null) => {
    if (isCancelledObject(obj)) {
      return 'Cancelled';
    }
    const status = obj?.status || 'QUEUE';
    switch (status) {
      case 'QUEUE': return 'Queued';
      case 'GENERATING_IMAGE': return 'Generating Image';
      case 'GENERATING_VIDEO': return 'Generating Video';
      case 'GENERATING_AUDIO': return 'Generating Audio';
      case 'COMPLETED': return 'Completed';
      case 'FAILED': return 'Failed';
      default: return status;
    }
  };

  const downloadConceptZip = async (concept: SceneConcept) => {
    if (!ensureSecret()) {
      return;
    }
    setError(null);
    if (!jobStatus) {
      setError('No job data available yet');
      return;
    }

    const conceptObjects = jobStatus.generation_objects.filter(
      (obj) => obj.concept_title === concept.name
    );

    if (conceptObjects.length === 0) {
      setError(`No generation results found for "${concept.name}"`);
      return;
    }

    setDownloadingConcepts((prev) => ({ ...prev, [concept.name]: true }));

    try {
      const zip = new JSZip();
      const videosFolder = zip.folder('videos');
      const metadataLines: string[] = [
        `Title: ${concept.name}`,
        `Description: ${concept.description}`,
        '',
        'Videos:'
      ];
      const missingEntries: string[] = [];

      for (const obj of conceptObjects) {
        const statusText = getStatusText(obj);
        const mediaPath = obj.audio_path || obj.video_path || '';
        const relativePath = mediaPath ? getRelativePath(mediaPath) : '';
        const fileName = relativePath ? relativePath.split('/').pop() : null;
        const mediaLabel = obj.audio_path ? ' (with audio)' : '';
        const contentLabel = getContentLabel(obj);
        const summary =
          obj.content_mode === 'scenario'
            ? getScenarioSummary(obj.activity_term, obj.place_term)
            : '';
        const displayName = summary ? `${contentLabel} — ${summary}` : contentLabel;

        metadataLines.push(
          `- ${displayName}: ${statusText}${fileName ? ` | ${fileName}${mediaLabel}` : ''}`
        );

        if (!relativePath || !fileName) {
          missingEntries.push(`${displayName} (no video available)`);
          continue;
        }

        const fileUrl = buildMediaUrl(mediaPath, false);
        if (!fileUrl) {
          missingEntries.push(`${displayName} (no video available)`);
          continue;
        }

        try {
          const response = await fetch(fileUrl, {
            headers: { 'X-Foxino-Token': apiSecret }
          });
          if (!response.ok) {
            missingEntries.push(`${displayName} (download failed: ${response.status})`);
            continue;
          }

          const data = await response.arrayBuffer();
          videosFolder?.file(fileName, data);
        } catch (downloadErr) {
          console.error(`Failed to download ${fileUrl}:`, downloadErr);
          missingEntries.push(`${displayName} (download error)`);
        }
      }

      if (missingEntries.length) {
        metadataLines.push('', 'Missing or unavailable videos:');
        missingEntries.forEach((entry) => metadataLines.push(`- ${entry}`));
      }

      zip.file('concept_info.txt', metadataLines.join('\n'));

      const conceptSlug =
        concept.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'concept';
      const timestamp = new Date().toISOString().split('T')[0];

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${conceptSlug}-${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to prepare concept ZIP:', err);
      setError('Failed to prepare download for this concept');
    } finally {
      setDownloadingConcepts((prev) => {
        const updated = { ...prev };
        delete updated[concept.name];
        return updated;
      });
    }
  };

  return (
    <div className="App">
      <header className="header">
        <h1>🦊 Foxino Video Style Generator</h1>
      </header>

      {secretOverlayVisible && (
        <div className="secret-overlay">
          <div className="secret-dialog">
            <h2>Enter Access Code</h2>
            <form className="secret-form" onSubmit={handleSecretSubmit}>
              <input
                type="password"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder="Shared secret"
                className="secret-input"
                autoFocus
                autoComplete="off"
              />
              {secretError && <p className="secret-error">{secretError}</p>}
              <button type="submit" className="secret-submit-btn">
                Unlock
              </button>
            </form>
            <p className="secret-helper">Contact the Foxino team for access.</p>
          </div>
        </div>
      )}

      <div className="container">
        {error && (
          <div className="error">
            {error}
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Step 1: Choose Concept Source */}
        <section className="section">
          <h2>1. Choose Concept Source</h2>
          <div className="concept-source-grid">
            {conceptModeOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`concept-source-card ${conceptMode === option.value ? 'active' : ''}`}
                onClick={() => handleConceptModeChange(option.value)}
              >
                <div className="concept-source-card-header">
                  <div className="concept-source-heading">
                    {option.icon && (
                      <span className="concept-source-icon">
                        {option.icon}
                      </span>
                    )}
                    <h3>{option.title}</h3>
                  </div>
                  {conceptMode === option.value && (
                    <span className="concept-source-badge">Selected</span>
                  )}
                </div>
                <p>{option.description}</p>
              </button>
            ))}
          </div>
        </section>

        {/* Step 2: Concept Intake */}
        {conceptMode === 'ai' && (
          <section className="section">
            <h2>2. Describe Your Theme</h2>
            <form
              className="input-group"
              onSubmit={(e) => {
                e.preventDefault();
                generateConcepts();
              }}
            >
              <input
                type="text"
                value={theme}
                onChange={(e) => setTheme(e.target.value)}
                placeholder="e.g., vintage animation, minimalist style, surreal colors..."
                className="theme-input"
              />
              <button
                type="submit"
                disabled={
                  !apiSecret ||
                  loading ||
                  !theme.trim() ||
                  contentItems.filter(item => item.enabled).length === 0
                }
                className="submit-btn"
              >
                {loading ? 'Generating...' : 'Generate Concepts'}
              </button>
            </form>
          </section>
        )}

        {conceptMode === 'manual' && (
          <section className="section">
            <h2>2. Add Manual Concepts</h2>
            <form
              className="manual-concept-form"
              onSubmit={(e) => {
                e.preventDefault();
                addManualConcept();
              }}
            >
              <input
                type="text"
                value={manualConceptTitle}
                onChange={(e) => setManualConceptTitle(e.target.value)}
                placeholder="Concept title"
                className="manual-input"
              />
              <textarea
                value={manualConceptDescription}
                onChange={(e) => setManualConceptDescription(e.target.value)}
                placeholder="Concept description"
                className="manual-textarea"
                rows={3}
              />
              <div className="manual-actions">
                <button type="submit" className="submit-btn manual-submit-btn">
                  Add Concept
                </button>
              </div>
            </form>
            <p className="helper-text">
              Added concepts appear below. Use the selection step to fine-tune which ones to generate.
            </p>
          </section>
        )}

        {conceptMode === 'preset' && (
          <section className="section">
            <h2>2. Choose From Style Library</h2>
            <p className="helper-text">
              Click any style to add or remove it. Added styles appear in the selection list below.
            </p>
            <div className="preset-grid">
              {PREDEFINED_STYLES.map((style) => {
                const isActive = concepts.some((concept) => concept.name === style.name);
                return (
                  <button
                    key={style.name}
                    type="button"
                    className={`preset-card ${isActive ? 'active' : ''}`}
                    onClick={() => togglePresetConcept(style)}
                  >
                    <div className="preset-card-header">
                      <h3>{style.name}</h3>
                      {isActive && <span className="preset-badge">Added</span>}
                    </div>
                    <p>{style.description}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 3: Concept Selection */}
        {concepts.length > 0 && (
          <section className="section">
            <h2>3. Select Concepts ({selectedConcepts.length} selected)</h2>
            <div className="concept-list">
              {concepts.map((concept, index) => (
                <div 
                  key={index}
                  className={`concept-item ${selectedConcepts.some(c => c.name === concept.name) ? 'selected' : ''}`}
                  onClick={() => toggleConceptSelection(concept)}
                >
                  <input
                    type="checkbox"
                    checked={selectedConcepts.some(c => c.name === concept.name)}
                    onChange={() => toggleConceptSelection(concept)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="concept-content">
                    <h3>{concept.name}</h3>
                    <p>{concept.description}</p>
                  </div>
                </div>
              ))}
            </div>
            {selectedConcepts.length > 0 && (
              <div className="style-parameters">
                <h3>Style Dynamics</h3>
                <p className="helper-text">
                  Fine-tune the generated prompt for the selected styles.
                </p>
                <div className="parameter-control">
                  <label htmlFor="color-intensity-slider">
                    Color Intensity <span>{colorIntensity}</span>
                  </label>
                  <input
                    id="color-intensity-slider"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={colorIntensity}
                    onChange={(event) => setColorIntensity(Number(event.target.value))}
                    style={sliderBackground(colorIntensity)}
                  />
                </div>
                <div className="parameter-control">
                  <label htmlFor="story-energy-slider">
                    Story Energy <span>{storyEnergy}</span>
                  </label>
                  <input
                    id="story-energy-slider"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={storyEnergy}
                    onChange={(event) => setStoryEnergy(Number(event.target.value))}
                    style={sliderBackground(storyEnergy)}
                  />
                </div>
                <div className="parameter-control">
                  <label htmlFor="surprise-factor-slider">
                    Surprise Factor <span>{surpriseFactor}</span>
                  </label>
                  <input
                    id="surprise-factor-slider"
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={surpriseFactor}
                    onChange={(event) => setSurpriseFactor(Number(event.target.value))}
                    style={sliderBackground(surpriseFactor)}
                  />
                </div>
              </div>
            )}
          </section>
        )}

        {/* Step 4: Content Selection */}
        {concepts.length > 0 && (
          <section className="section">
            <h2>4. Select Content to Generate ({contentItems.filter(item => item.enabled).length} selected)</h2>

            <div className="content-mode-toggle">
              <button
                type="button"
                className={`mode-toggle-btn ${contentMode === 'single_word' ? 'active' : ''}`}
                onClick={() => handleContentModeChange('single_word')}
                aria-pressed={contentMode === 'single_word'}
              >
                <span className="mode-toggle-title">Single Word</span>
                <span className="mode-toggle-caption">Great for vocabulary lists and flashcards.</span>
              </button>
              <button
                type="button"
                className={`mode-toggle-btn ${contentMode === 'scenario' ? 'active' : ''}`}
                onClick={() => handleContentModeChange('scenario')}
                aria-pressed={contentMode === 'scenario'}
              >
                <span className="mode-toggle-title">Object • Activity • Place</span>
                <span className="mode-toggle-caption">Describe a scene to show motion in context.</span>
              </button>
            </div>

            <p className="helper-text">
              {contentMode === 'single_word'
                ? 'Choose up to 10 vocabulary words to illustrate.'
                : scenarioFlow === 'manual'
                  ? 'Describe up to 10 custom scenes where your object performs an activity in a place.'
                  : 'Enter word banks for each category and let Foxino assemble up to 10 random scenes.'}
            </p>

            {contentMode === 'single_word' ? (
              <>
                <div className="add-word-form">
                  <form
                    className="input-group"
                    onSubmit={(e) => {
                      e.preventDefault();
                      addSingleWordItem();
                    }}
                  >
                    <input
                      type="text"
                      value={newWord}
                      onChange={(e) => setNewWord(e.target.value)}
                      placeholder="Word (e.g., elephant)"
                      className="word-input"
                    />
                    <button
                      type="submit"
                      disabled={contentItems.length >= MAX_CONTENT_ITEMS}
                      className="add-word-btn"
                    >
                      Add Word
                    </button>
                  </form>
                  <p className="word-limit">
                    Maximum {MAX_CONTENT_ITEMS} words ({contentItems.length}/{MAX_CONTENT_ITEMS})
                  </p>
                </div>

                {contentItems.length === 0 && (
                  <p className="word-empty-hint">No words yet. Add one using the form above.</p>
                )}

                <div className="word-list">
                  {contentItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="word-item-selectable"
                      onClick={() => toggleContentItemEnabled(index)}
                    >
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={() => toggleContentItemEnabled(index)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="word-content">
                        <span className="word-text">{item.word}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeContentItem(index);
                        }}
                        className="remove-word-btn"
                        title="Remove word"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="scenario-flow-toggle">
                  <button
                    type="button"
                    className={`scenario-flow-btn ${scenarioFlow === 'manual' ? 'active' : ''}`}
                    onClick={() => handleScenarioFlowChange('manual')}
                    aria-pressed={scenarioFlow === 'manual'}
                  >
                    <span className="scenario-flow-title">Manual Scenes</span>
                    <span className="scenario-flow-caption">
                      Type each object, activity, and place yourself.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`scenario-flow-btn ${scenarioFlow === 'random' ? 'active' : ''}`}
                    onClick={() => handleScenarioFlowChange('random')}
                    aria-pressed={scenarioFlow === 'random'}
                  >
                    <span className="scenario-flow-title">Auto-Mix Scenes</span>
                    <span className="scenario-flow-caption">
                      Add word banks and we’ll shuffle unique combinations.
                    </span>
                  </button>
                </div>

                {scenarioFlow === 'manual' ? (
                  <div className="add-word-form">
                    <form
                      className="scenario-input-group"
                      onSubmit={(e) => {
                        e.preventDefault();
                        addScenarioItem();
                      }}
                    >
                      <input
                        type="text"
                        value={newScenarioObject}
                        onChange={(e) => setNewScenarioObject(e.target.value)}
                        placeholder="Object (e.g., robot cat)"
                        className="word-input"
                      />
                      <input
                        type="text"
                        value={newScenarioActivity}
                        onChange={(e) => setNewScenarioActivity(e.target.value)}
                        placeholder="Activity (e.g., rides a skateboard)"
                        className="word-input"
                      />
                      <input
                        type="text"
                        value={newScenarioPlace}
                        onChange={(e) => setNewScenarioPlace(e.target.value)}
                        placeholder="Place (e.g., neon city street)"
                        className="word-input"
                      />
                      <button
                        type="submit"
                        disabled={contentItems.length >= MAX_CONTENT_ITEMS}
                        className="add-word-btn"
                      >
                        Add Scene
                      </button>
                    </form>
                    <p className="word-limit">
                      Maximum {MAX_CONTENT_ITEMS} scenes ({contentItems.length}/{MAX_CONTENT_ITEMS})
                    </p>
                  </div>
                ) : (
                  <div className="scenario-random-panel">
                    <div className="scenario-random-grid">
                      <div className="scenario-random-column">
                        <h3>Objects</h3>
                        <form
                          className="scenario-pool-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            addScenarioPoolValue('object');
                          }}
                        >
                          <input
                            type="text"
                            value={randomObjectInput}
                            onChange={(e) => setRandomObjectInput(e.target.value)}
                            placeholder="e.g., robot cat"
                            className="word-input"
                          />
                          <button type="submit" className="add-word-btn">
                            Add
                          </button>
                        </form>
                        <div className="scenario-chip-list">
                          {scenarioObjects.map((value, index) => (
                            <span key={`${value}-${index}`} className="scenario-chip">
                              {value}
                              <button
                                type="button"
                                aria-label={`Remove object ${value}`}
                                onClick={() => removeScenarioPoolValue('object', index)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="scenario-random-column">
                        <h3>Activities</h3>
                        <form
                          className="scenario-pool-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            addScenarioPoolValue('activity');
                          }}
                        >
                          <input
                            type="text"
                            value={randomActivityInput}
                            onChange={(e) => setRandomActivityInput(e.target.value)}
                            placeholder="e.g., rides a skateboard"
                            className="word-input"
                          />
                          <button type="submit" className="add-word-btn">
                            Add
                          </button>
                        </form>
                        <div className="scenario-chip-list">
                          {scenarioActivities.map((value, index) => (
                            <span key={`${value}-${index}`} className="scenario-chip">
                              {value}
                              <button
                                type="button"
                                aria-label={`Remove activity ${value}`}
                                onClick={() => removeScenarioPoolValue('activity', index)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="scenario-random-column">
                        <h3>Places</h3>
                        <form
                          className="scenario-pool-form"
                          onSubmit={(e) => {
                            e.preventDefault();
                            addScenarioPoolValue('place');
                          }}
                        >
                          <input
                            type="text"
                            value={randomPlaceInput}
                            onChange={(e) => setRandomPlaceInput(e.target.value)}
                            placeholder="e.g., neon city street"
                            className="word-input"
                          />
                          <button type="submit" className="add-word-btn">
                            Add
                          </button>
                        </form>
                        <div className="scenario-chip-list">
                          {scenarioPlaces.map((value, index) => (
                            <span key={`${value}-${index}`} className="scenario-chip">
                              {value}
                              <button
                                type="button"
                                aria-label={`Remove place ${value}`}
                                onClick={() => removeScenarioPoolValue('place', index)}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="scenario-random-controls">
                      <label className="scenario-count-label">
                        Scenes to generate (max {MAX_CONTENT_ITEMS})
                        <input
                          type="number"
                          min={1}
                          max={MAX_CONTENT_ITEMS}
                          value={randomScenarioCount}
                          onChange={(e) =>
                            setRandomScenarioCount(() => {
                              const rawValue = Number(e.target.value);
                              const baseValue = Number.isFinite(rawValue) ? Math.floor(rawValue) : 1;
                              return Math.max(1, Math.min(MAX_CONTENT_ITEMS, baseValue || 1));
                            })
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className="submit-btn generate-scenes-btn"
                        onClick={generateRandomScenarios}
                        disabled={
                          !scenarioObjects.length ||
                          !scenarioActivities.length ||
                          !scenarioPlaces.length
                        }
                      >
                        Generate Scenes
                      </button>
                    </div>
                    <p className="scenario-random-hint">
                      Each click shuffles unique combinations from your word banks.
                    </p>
                  </div>
                )}

                {contentItems.length === 0 && (
                  <p className="word-empty-hint">
                    {scenarioFlow === 'manual'
                      ? 'No scenes yet. Add one using the form above.'
                      : 'No scenes yet. Add words to each column and click Generate Scenes.'}
                  </p>
                )}

                <div className="word-list">
                  {contentItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="word-item-selectable"
                      onClick={() => toggleContentItemEnabled(index)}
                    >
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={() => toggleContentItemEnabled(index)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {(() => {
                        const sceneLabel =
                          (item.objectTerm ?? '').trim() || (item.word ?? 'Scene');
                        const summary = getScenarioSummary(
                          item.activityTerm,
                          item.placeTerm
                        );
                        return (
                          <div className="word-content">
                            <span className="word-text">{sceneLabel}</span>
                            {summary && <span className="word-topic">{summary}</span>}
                          </div>
                        );
                      })()}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeContentItem(index);
                        }}
                        className="remove-word-btn"
                        title="Remove scene"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        )}

        {/* Step 5: Start Generation */}
        {concepts.length > 0 && (
          <section className="section">
            <h2>5. Start Generation</h2>
            <button 
              onClick={submitJob} 
              disabled={
                !apiSecret ||
                loading ||
                selectedConcepts.length === 0 ||
                contentItems.filter(item => item.enabled).length === 0
              }
              className="submit-btn"
            >
              {loading ? 'Submitting...' : 'Start Generation'}
            </button>
          </section>
        )}

        {/* Step 6: Generation Gallery */}
        {jobStatus && (
          <section className="section">
            <h2>6. Generation Progress</h2>
            <div className="job-info">
              <p>Job ID: {jobStatus.job_id} | Status: {jobStatus.status}</p>
              <p>
                Progress:{' '}
                {jobStatus.completed_objects + jobStatus.failed_objects + jobStatus.cancelled_objects}
                /{jobStatus.total_objects} processed
              </p>
              <p className="job-breakdown">
                Completed: {jobStatus.completed_objects} | Failed: {jobStatus.failed_objects} | Cancelled:{' '}
                {jobStatus.cancelled_objects}
              </p>
              {jobStatus.status === 'PROCESSING' && (
                <button 
                  onClick={cancelJob}
                  disabled={!apiSecret || cancelling}
                  className="cancel-btn"
                  style={{
                    backgroundColor: '#F44336',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: cancelling ? 'not-allowed' : 'pointer',
                    marginTop: '10px'
                  }}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Job'}
                </button>
              )}
            </div>
            <div className="gallery-table">
              {selectedConcepts.map((concept) => {
                const conceptObjects = jobStatus.generation_objects.filter(
                  (obj) => obj.concept_title === concept.name
                );
                const activeObjects = conceptObjects.filter(
                  (obj) => !['COMPLETED', 'FAILED'].includes(obj.status || '')
                );
                const isCancellingConcept = Boolean(cancellingConcepts[concept.name]);
                const cancelDisabled =
                  !apiSecret ||
                  !jobId ||
                  isCancellingConcept ||
                  activeObjects.length === 0;

                return (
                  <div key={concept.name} className="concept-row">
                    <div className="concept-header">
                      <div className="concept-meta">
                        <h3>{concept.name}</h3>
                        <p>{concept.description}</p>
                      </div>
                      <div className="concept-header-actions">
                        <button
                          className="cancel-concept-btn"
                          onClick={() => cancelConcept(concept.name)}
                          disabled={cancelDisabled}
                        >
                          {isCancellingConcept ? 'Cancelling...' : 'Cancel Prompt'}
                        </button>
                        <button
                          className="download-zip-btn"
                          onClick={() => downloadConceptZip(concept)}
                          disabled={
                            !apiSecret ||
                            Boolean(downloadingConcepts[concept.name]) ||
                            isCancellingConcept ||
                            conceptObjects.some(
                              (obj) =>
                                !['COMPLETED', 'FAILED'].includes(obj.status || '')
                            )
                          }
                        >
                          {downloadingConcepts[concept.name]
                            ? 'Preparing ZIP...'
                            : 'Download Videos ZIP'}
                        </button>
                      </div>
                    </div>
                    <div className="words-row">
                      {[...conceptObjects]
                        .sort((a, b) => a.filename_prefix.localeCompare(b.filename_prefix))
                        .map((obj) => {
                          const audioSrc = buildMediaUrl(obj?.audio_path ?? null);
                          const videoSrc = buildMediaUrl(obj?.video_path ?? null);
                          const imageSrc = buildMediaUrl(obj?.image_path ?? null);
                          const label = getContentLabel(obj);
                          const summary =
                            obj?.content_mode === 'scenario'
                              ? getScenarioSummary(obj?.activity_term, obj?.place_term)
                              : '';

                          return (
                            <div key={`${concept.name}-${obj?.id}`} className="word-item">
                              <div className="word-header">
                                <h4>{label}</h4>
                                <span 
                                  className="status-badge"
                                  style={{ backgroundColor: getStatusColor(obj) }}
                                >
                                  {getStatusText(obj)}
                                </span>
                              </div>
                              {summary && (
                                <p className="word-topic">{summary}</p>
                              )}
                              <div className="word-content">
                                {obj?.error_message && (
                                  <p className="error-message"><strong>Error:</strong> {obj.error_message}</p>
                                )}
                                {audioSrc ? (
                                  <div className="media-preview">
                                    <video key={audioSrc} controls className="preview-video">
                                      <source src={audioSrc} type="video/mp4" />
                                    </video>
                                  </div>
                                ) : videoSrc ? (
                                  <div className="media-preview">
                                    <video key={videoSrc} controls className="preview-video">
                                      <source src={videoSrc} type="video/mp4" />
                                    </video>
                                  </div>
                                ) : imageSrc ? (
                                  <div className="media-preview">
                                    <img src={imageSrc} alt="Generated" className="preview-image" />
                                  </div>
                                ) : (
                                  <div className="placeholder">
                                    <p>Waiting in queue...</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 7: Save or Export Concepts */}
        {selectedConcepts.length > 0 && (
          <section className="section">
            <h2>7. Save or Export Concepts ({conceptsToExport.length} selected)</h2>
            <div className="concept-list">
              {selectedConcepts.map((concept, index) => {
                const isSelectedForExport = conceptsToExport.some(c => c.name === concept.name);
                return (
                  <div 
                    key={index} 
                    className={`concept-item ${isSelectedForExport ? 'selected' : ''}`}
                    onClick={() => toggleExportConcept(concept)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelectedForExport}
                      onChange={() => toggleExportConcept(concept)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="concept-content">
                      <h3>{concept.name}</h3>
                      <p>{concept.description}</p>
                      <textarea
                        className="note-input"
                        placeholder="Add a note"
                        value={conceptNotes[concept.name] ?? ''}
                        onChange={(e) => updateConceptNote(concept.name, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        rows={2}
                        disabled={!isSelectedForExport}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="concept-actions">
              <button 
                onClick={saveConcepts} 
                disabled={
                  !apiSecret ||
                  conceptsToExport.length === 0 ||
                  savingConcepts
                }
                className="submit-btn"
              >
                {savingConcepts ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={exportConcepts}
                disabled={
                  !apiSecret ||
                  conceptsToExport.length === 0 ||
                  exportingConcepts
                }
                className="secondary-btn"
              >
                {exportingConcepts ? 'Exporting...' : 'Export CSV'}
              </button>
            </div>
          </section>
        )}

        {/* Clear Button */}
        <div className="actions">
          <button onClick={clearAll} className="clear-btn">
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
